import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';

export const useCalendarEvents = (selectedDate) => {
  const { isAuthenticated, token, loading: authLoading, getToken } = useAuth();
  const lastFetchedDate = useRef(null);
  const isCurrentlyFetching = useRef(false);
  const eventCache = useRef(new Map()); // Cache events by date
  const [eventData, setEventData] = useState({
    events: [],
    eventsByCalendar: {},
    loading: false,
    error: null,
    lastFetch: null,
    cachedDate: null
  });

  const fetchEvents = useCallback(async (date) => {
    if (!isAuthenticated || !token || !date) {
      return;
    }

    // Format date as YYYY-MM-DD
    const dateString = typeof date === 'string' ? date : date.toISOString().split('T')[0];

    // Check cache first
    const cachedData = eventCache.current.get(dateString);
    if (cachedData && Date.now() - cachedData.timestamp < 2 * 60 * 1000) { // 2 minute cache
      setEventData({
        ...cachedData.data,
        cachedDate: dateString
      });
      lastFetchedDate.current = dateString;
      return;
    }

    // Prevent concurrent fetches for the same date
    if (isCurrentlyFetching.current === dateString) {
      return;
    }

    try {
      isCurrentlyFetching.current = dateString;
      setEventData(prev => ({ ...prev, loading: true, error: null }));

      // Get fresh token for each request to ensure it's not expired
      const freshToken = await getToken();
      
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${freshToken || token}`
      };

      const response = await fetch(`/api/google/events/fetch?date=${dateString}`, {
        method: 'GET',
        headers
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 errors with automatic retry using fresh token
        if (response.status === 401) {
          
          try {
            // Get a fresh token and retry once
            const retryToken = await getToken();
            const retryHeaders = { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${retryToken}`
            };

            const retryResponse = await fetch(`/api/google/events/fetch?date=${dateString}`, {
              method: 'GET',
              headers: retryHeaders
            });

            const retryData = await retryResponse.json();

            if (retryResponse.ok) {
              // Retry succeeded, process the data
              const newEventData = {
                events: retryData.events || [],
                eventsByCalendar: retryData.eventsByCalendar || {},
                loading: false,
                error: null,
                lastFetch: new Date(),
                cachedDate: dateString
              };
              
              // Cache the data
              eventCache.current.set(dateString, {
                data: newEventData,
                timestamp: Date.now()
              });

              lastFetchedDate.current = dateString;
              setEventData(newEventData);
              return;
            }
          } catch (retryError) {
            console.error('Retry also failed:', retryError);
          }
        }
        
        // Handle specific error cases
        if (data.requiresAuth || data.requiresReauth) {
          setEventData(prev => ({
            ...prev,
            loading: false,
            error: 'calendar_auth_required',
            events: [],
            eventsByCalendar: {}
          }));
          return;
        }
        
        // Handle authorization expired errors
        if (response.status === 401 && data.error && 
            (data.error.includes('authorization expired') || 
             data.error.includes('Please reconnect'))) {
          setEventData(prev => ({
            ...prev,
            loading: false,
            error: 'calendar_auth_expired',
            events: [],
            eventsByCalendar: {}
          }));
          return;
        }
        
        throw new Error(data.error || 'Failed to fetch calendar events');
      }

      const newEventData = {
        events: data.events || [],
        eventsByCalendar: data.eventsByCalendar || {},
        loading: false,
        error: null,
        lastFetch: new Date(),
        cachedDate: dateString
      };
      
      // Cache the data
      eventCache.current.set(dateString, {
        data: newEventData,
        timestamp: Date.now()
      });

      // Clean old cache entries (keep only last 7 days)
      if (eventCache.current.size > 7) {
        const sortedEntries = Array.from(eventCache.current.entries())
          .sort((a, b) => b[1].timestamp - a[1].timestamp);
        eventCache.current.clear();
        sortedEntries.slice(0, 7).forEach(([key, value]) => {
          eventCache.current.set(key, value);
        });
      }
      
      // Update the ref to track the last fetched date
      lastFetchedDate.current = dateString;
      setEventData(newEventData);

    } catch (error) {
      console.error('Error fetching calendar events:', error);
      
      // Handle specific error cases gracefully
      let errorMessage = error.message;
      if (error.message.includes('Google Calendar not connected') || 
          error.message.includes('Authentication required') ||
          error.message.includes('authorization expired')) {
        errorMessage = 'google_calendar_not_connected';
      }
      
      setEventData(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        events: [],
        eventsByCalendar: {}
      }));
    } finally {
      isCurrentlyFetching.current = false;
    }
  }, [isAuthenticated, token, getToken]);

  const refreshEvents = useCallback(() => {
    if (selectedDate) {
      // Clear cache for this date to force a fresh fetch
      const dateString = typeof selectedDate === 'string' 
        ? selectedDate 
        : selectedDate.toISOString().split('T')[0];
      eventCache.current.delete(dateString);
      fetchEvents(selectedDate);
    }
  }, [selectedDate, fetchEvents]);

  const clearEvents = useCallback(() => {
    console.log('clearEvents called - clearing all event data');
    lastFetchedDate.current = null;
    isCurrentlyFetching.current = false;
    eventCache.current.clear();
    setEventData({
      events: [],
      eventsByCalendar: {},
      loading: false,
      error: null,
      lastFetch: null,
      cachedDate: null
    });
  }, []);

  // Fetch events when date changes or auth state changes
  useEffect(() => {
    if (!authLoading && selectedDate && isAuthenticated && token) {
      const dateString = typeof selectedDate === 'string' 
        ? selectedDate 
        : selectedDate.toISOString().split('T')[0];
      
      // Check if we have fresh cached data for this date
      const cachedData = eventCache.current.get(dateString);
      const isCacheFresh = cachedData && Date.now() - cachedData.timestamp < 2 * 60 * 1000;
      
      // Fetch if we don't have cached data for this date or cache is stale
      if (lastFetchedDate.current !== dateString || !isCacheFresh) {
        fetchEvents(selectedDate);
      }
    }
  }, [selectedDate, authLoading, isAuthenticated, token, fetchEvents]);

  // Clear events when user logs out - DISABLED due to auth state issues
  // useEffect(() => {
  //   console.log('clearEvents useEffect triggered:', { isAuthenticated });
  //   if (!isAuthenticated) {
  //     console.log('User not authenticated - clearing events');
  //     clearEvents();
  //   } else {
  //     console.log('User is authenticated - NOT clearing events');
  //   }
  // }, [isAuthenticated]);

  const getEventsForTimeSlot = useCallback((hour, minute = 0) => {
    if (!eventData.events.length) return [];

    return eventData.events.filter(event => {
      if (event.allDay) return false; // Don't show all-day events in time slots
      
      const startTime = new Date(event.startTime);
      const endTime = new Date(event.endTime);
      const slotTime = new Date(selectedDate);
      slotTime.setHours(hour, minute, 0, 0);
      
      return startTime <= slotTime && endTime > slotTime;
    });
  }, [eventData.events, selectedDate]);

  const getAllDayEvents = useCallback(() => {
    if (!eventData.events.length) return [];
    
    return eventData.events.filter(event => event.allDay);
  }, [eventData.events]);

  const getEventsForCalendar = useCallback((calendarId) => {
    return eventData.eventsByCalendar[calendarId]?.events || [];
  }, [eventData.eventsByCalendar]);

  const hasEventsForDate = useCallback(() => {
    return eventData.events.length > 0;
  }, [eventData.events]);

  // Clear cache for today's date (to get fresh events)
  const clearTodayCache = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    eventCache.current.delete(today);
    if (selectedDate && selectedDate.includes && selectedDate.includes(today)) {
      fetchEvents(selectedDate);
    } else if (selectedDate && typeof selectedDate !== 'string') {
      const selectedDateStr = selectedDate.toISOString().split('T')[0];
      if (selectedDateStr === today) {
        fetchEvents(selectedDate);
      }
    }
  }, [selectedDate, fetchEvents]);

  const returnValue = {
    ...eventData,
    fetchEvents,
    refreshEvents,
    clearEvents,
    clearTodayCache,
    
    // Helper methods
    getEventsForTimeSlot,
    getAllDayEvents,
    getEventsForCalendar,
    hasEventsForDate,
    
    // Status helpers
    isCalendarAuthRequired: eventData.error === 'calendar_auth_required',
    hasEvents: eventData.events.length > 0,
    isEmpty: !eventData.loading && eventData.events.length === 0 && !eventData.error,
    
    // Calendar-specific data
    calendarsWithEvents: Object.keys(eventData.eventsByCalendar),
    totalEvents: eventData.events.length
  };
  
  // Removed excessive console logging to prevent loop
  
  return returnValue;
};